import {
  appsBomApiGetGlobalNomenclatureTree,
  getAppsBomApiGetGlobalNomenclatureTreeQueryKey,
} from '@/lib/api/generated/endpoints'
import type { GlobalNomenclatureTreeOut } from '@/lib/api/generated/types.ts/globalNomenclatureTreeOut'
import type { QueryClient } from '@tanstack/react-query'
import { queryOptions } from '@tanstack/react-query'

/**
 * Cross-project nomenclature aggregator. Backed by
 * `GET /api/global-nomenclature-tree/?include_inactive=true`.
 *
 * Powers the 1С-style global page. Inactive entities (archived projects,
 * folders, parts, assemblies, materials) are returned alongside active ones
 * so the user can find / reactivate them; the row renderer fades inactive
 * rows (`!isActive → opacity-60`) instead of hiding them.
 *
 * orval's customFetch returns the parsed body directly (per
 * `lib/api/fetcher.ts`); cast at queryFn level — runtime shape matches
 * `GlobalNomenclatureTreeOut`.
 */
export const globalNomenclatureTreeQuery = () =>
  queryOptions({
    queryKey: getAppsBomApiGetGlobalNomenclatureTreeQueryKey({ include_inactive: true }),
    queryFn: async ({ signal }) =>
      (await appsBomApiGetGlobalNomenclatureTree(
        { include_inactive: true },
        { signal },
      )) as unknown as GlobalNomenclatureTreeOut,
    staleTime: 30_000,
  })

/** Match every cached variant of the global tree (active-only + archive). */
export function invalidateGlobalNomenclatureTree(qc: QueryClient): void {
  qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === '/api/global-nomenclature-tree/',
  })
}
